
import React from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, CheckCircle2, Crown } from "lucide-react";
import { PlanFeature, ButtonConfig } from "../types";
import { LoadingButton } from "@/components/ui/loading-button";
import { PlanTierOption } from "@/types/subscription";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from "@/components/AuthProvider";

interface PlanCardProps {
  title: string;
  price: number;
  features: PlanFeature[];
  isCurrentPlan: boolean;
  buttonConfig: ButtonConfig;
  isLoading: boolean;
  onSubscribe: () => void;
  hasTrial?: boolean;
  isRecommended?: boolean;
  selectedTier?: PlanTierOption;
  isCurrentTier?: boolean;
  isComingSoon?: boolean;
  hidePriceDisplay?: boolean;
}

export const PlanCard = ({
  title,
  price,
  features,
  isCurrentPlan,
  buttonConfig,
  isLoading,
  onSubscribe,
  hasTrial = false,
  isRecommended = false,
  selectedTier,
  isCurrentTier = false,
  isComingSoon = false,
  hidePriceDisplay = false
}: PlanCardProps) => {
  const { language } = useLanguage();
  const { t: dashboardT } = useTranslations("dashboard");
  const { profile } = useAuth();
  
  const isTrial = profile?.subscription_plan === 'trial';
  const showTrialMessage = hasTrial && !selectedTier?.tier?.includes('pro_10');
  
  const renderPrice = () => {
    if (isComingSoon || price === 0 || hidePriceDisplay) {
      return null;
    }
    return (
      <div className="text-3xl font-bold mb-2">
        €{price.toFixed(2)}
        <span className="text-sm font-normal">{dashboardT('subscription.perMonth')}</span>
      </div>
    );
  };

  return (
    <Card className={`relative border ${isCurrentPlan ? "border-primary" : isRecommended ? "border-flame" : ""}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-flame text-white px-4 py-1 rounded-full text-sm font-medium">
            {dashboardT('subscription.recommended')}
          </span>
        </div>
      )}
      
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isCurrentPlan && <CheckCircle2 className="h-5 w-5 text-primary" />}
          {title}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {renderPrice()}
        
        {isComingSoon && (
          <div className="text-lg font-medium text-blue-600 dark:text-blue-400">
            {language === 'pt' ? 'Em Breve' : 'Coming Soon'}
          </div>
        )}
        
        {showTrialMessage && (
          <div className="text-sm text-primary font-medium">
            {dashboardT('subscription.freeDays')}
          </div>
        )}
        
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
              <span className="text-sm">
                {dashboardT(feature.text)}
                {feature.comingSoon && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({language === 'pt' ? 'Em Breve' : 'Coming Soon'})
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className="flex flex-col items-center">
        {buttonConfig ? (
          isComingSoon ? (
            <Button variant="outline" className="w-full" disabled>
              {language === 'pt' ? 'Em Breve' : 'Coming Soon'}
            </Button>
          ) : isCurrentTier ? (
            <Button variant="outline" className="w-full" disabled>
              {dashboardT('subscription.buttons.currentPlan')}
            </Button>
          ) : (
            <LoadingButton 
              loading={isLoading} 
              variant={buttonConfig.variant as ButtonProps["variant"]} 
              onClick={onSubscribe} 
              disabled={isCurrentPlan && !hasTrial && isCurrentTier}
              className="w-full">
              {isTrial 
                ? dashboardT('subscription.buttons.upgrade')
                : hasTrial && !isCurrentPlan 
                  ? dashboardT('subscription.buttons.upgrade')
                  : dashboardT(buttonConfig.text)}
            </LoadingButton>
          )
        ) : null}
      </CardFooter>
    </Card>
  );
};
